import { ElementRef, Injector } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  CommentLogEntry,
  CultureService,
  DomainConfigBase,
  FlowImportanceKey,
  FlowInfoData,
  InputHelperService,
  IQformListInput,
  ItemScope,
  ListOption,
  QformAutocompleteItem,
  QformCard,
  QformDomainOptionsRequestedEvent,
  QformDomainOptionsRetrievedEvent,
  QformEvent,
  QformEventBus,
  QformFilePickerInput,
  QformInputBase,
  QformItemSelectorInput,
  QformRole,
  QformRoleOptionsRequestedEvent,
  QformRoleOptionsRetrievedEvent,
  ViewMode,
} from 'qflow-form-renderer';
import { BaseComponent } from '../../../core/base.component';
import {
  AttachmentsScopeOutputDto,
  CommentScopeOutputDto,
  FlowCommentOutputDto,
  FlowDataOutputDto,
  FlowRoleInputDto,
  FlowRoleOutputDto,
  GuidStringKeyValuePair,
  RoleMemberInputDto,
  StringStringKeyValuePair,
  TemplateRoleMemberOutputDto,
} from '../../../core/remote-clients/remote-client';
import { LocalizationService } from '../../../core/services/localization.service';
import { RedirectService } from '../../../core/services/redirect.service';
import { SessionService } from '../../../core/services/session.service';
import { ComponentData } from '../../../models/component-data.model';
import { ComponentDataService } from '../../../services/component-data.service';
import {
  DomainDependencyParserService,
  InputDependency,
} from '../../../services/data-dependencies/domain-dependency-parser.service';
import { DataDependencyService } from '../../../services/data-dependencies/data-dependency.service';
import {
  DataDomainDependency,
  RemoteDataDomainService,
} from '../../../services/remote/remote-data-domain.service';
import { RemoteFlowService } from '../../../services/remote/remote-flow.service';
import { RemoteRoleService } from '../../../services/remote/remote-role.service';

export abstract class InteractiveStepBaseComponent extends BaseComponent {
  private static readonly FORMAT_COMPONENT_TYPES = new Set([
    'Qform.Element.Title',
    'Qform.Element.Message',
    'Qform.FlowElement.FlowInfo',
  ]);
  protected readonly _localizationService: LocalizationService;
  protected readonly _componentDataService: ComponentDataService;
  protected readonly _route: ActivatedRoute;
  protected readonly _dataDomainService: RemoteDataDomainService;
  protected readonly _roleService: RemoteRoleService;
  protected readonly _flowService: RemoteFlowService;
  protected readonly _redirectService: RedirectService;
  protected readonly isGuest: boolean = false;
  protected readonly tenantId: string;

  protected readonly _cultureService: CultureService;
  private readonly _sessionService: SessionService;
  private readonly _dependencyParser: DomainDependencyParserService;
  private readonly _elementRef: ElementRef;

  private readonly _dataDependencies: DataDependencyService;
  private _cascadeListenerSetup = false;

  versionId!: string;
  qformCard: QformCard = new QformCard();
  formComponents: ComponentData[] = [];
  commentsScope: CommentScopeOutputDto | undefined;
  comments: CommentLogEntry[] = [];
  attachmentsScope: AttachmentsScopeOutputDto | undefined;

  constructor(injector: Injector) {
    super(injector);
    this._localizationService = injector.get(LocalizationService);
    this._componentDataService = injector.get(ComponentDataService);
    this._route = injector.get(ActivatedRoute);
    this._cultureService = injector.get(CultureService);
    this._sessionService = injector.get(SessionService);
    this._roleService = injector.get(RemoteRoleService);
    this._dataDomainService = injector.get(RemoteDataDomainService);
    this._flowService = injector.get(RemoteFlowService);
    this._redirectService = injector.get(RedirectService);
    this._dependencyParser = injector.get(DomainDependencyParserService);
    this._elementRef = injector.get(ElementRef);

    this.tenantId = this._route.snapshot.queryParamMap.get('tenantId') ?? '';
    this.isGuest = this._route.snapshot.data['isGuest'];

    this._dataDependencies = new DataDependencyService({
      getInputs: () => this.qformCard.getInputs(),
      fetchDataSourceOptions: this.fetchDataSourceOptions.bind(this),
      initializeListInput: this.initializeListInput.bind(this),
    });

    this.configureFormEvents(injector);
  }

  protected initDependencies(
    formJson: any,
    flowData?: readonly FlowDataOutputDto[] | null
  ): void {
    const maps = this._dependencyParser.parseDependencies(formJson, flowData);
    this._dataDependencies.initDependencies(
      maps.input,
      maps.sourceToTargets,
      maps.outputBySourceControl
    );
    this._cascadeListenerSetup = false;
  }

  private configureFormEvents(injector: Injector): void {
    const eventBus = injector.get(QformEventBus);
    eventBus.listen(
      QformEvent.RoleOptionsRequested,
      async (args: QformRoleOptionsRequestedEvent) =>
        await this.handleRoleOptionsRequested(args, eventBus)
    );

    eventBus.listen(
      QformEvent.DomainOptionsRequested,
      async (args: QformDomainOptionsRequestedEvent) =>
        await this.handleDomainOptionsRequested(args, eventBus)
    );
  }

  private async handleRoleOptionsRequested(
    args: QformRoleOptionsRequestedEvent,
    eventBus: QformEventBus
  ): Promise<void> {
    const roleMembers = await this._roleService.getPossibleRoleMembers(
      args.qflowId,
      this.versionId,
      args.term
    );

    const mapRoleToAutocompleteItem = (role: TemplateRoleMemberOutputDto) =>
      new QformAutocompleteItem(
        role.id ?? '',
        role.name ?? '',
        role.templateRoleMemberType ?? 0
      );
    const event = new QformRoleOptionsRetrievedEvent(
      args.qflowId,
      roleMembers.map(mapRoleToAutocompleteItem)
    );

    eventBus.send(QformEvent.RoleOptionsRetrieved, event);
  }

  private async handleDomainOptionsRequested(
    args: QformDomainOptionsRequestedEvent,
    eventBus: QformEventBus
  ): Promise<void> {
    try {
      const inputDeps = this._dataDependencies.getInputDependencies(args.qflowId);
      const dependencies = this.resolveDependencies(inputDeps);

      const dataSource = await this._dataDomainService.getDataSourceWithDependencies(
        args.domainId,
        this.versionId,
        dependencies,
        args.term
      );

      const rows = dataSource?.optionRowColumnValues ?? [];
      const mapDataSourceOptionToAutocompleteItem = (
        option: StringStringKeyValuePair,
        index: number
      ) =>
        new QformAutocompleteItem(
          option.key ?? '',
          option.value ?? '',
          undefined,
          undefined,
          rows[index]
        );
      const opts = (dataSource?.options ?? [])
        .map((option, index) => ({ option, index }))
        .filter(entry => entry.option != null);
      const event = new QformDomainOptionsRetrievedEvent(
        args.domainId,
        args.qflowId,
        opts.map(entry =>
          mapDataSourceOptionToAutocompleteItem(entry.option, entry.index)
        )
      );

      eventBus.send(QformEvent.DomainOptionsRetrieved, event);
    } catch {
      eventBus.send(
        QformEvent.DomainOptionsRetrieved,
        new QformDomainOptionsRetrievedEvent(args.domainId, args.qflowId, [])
      );
    }
  }

  protected async fillDataControlWithValueAndScope(
    datas: FlowDataOutputDto[]
  ): Promise<void> {
    this._cultureService.setCurrentCulture(
      this._localizationService.currentCulture
    );

    const currentUser = await this._sessionService.getCurrentUser();
    if (currentUser?.timeZone) {
      this._cultureService.currentTimeZone = currentUser.timeZone;
    }

    const cardInputs: QformInputBase[] = this.qformCard.getInputs();

    datas.forEach(d => {
      const inputToSetValue = cardInputs.find(
        input => input.qflowId === d.id
      ) as any;

      if (inputToSetValue) {
        inputToSetValue.setControlValue(d.value);
        inputToSetValue.domainId = d.domain!.id!;
        inputToSetValue.scope = d.scope;
      }
    });

    const dataIds = datas.map(d => d.id);

    cardInputs.forEach(input => {
      if (!dataIds.includes(input.qflowId)) {
        input.scope = ItemScope.Missing;
      }
    });

    await this.resolveItemSelectorInitialColumnValues(datas);
  }

  private async resolveItemSelectorInitialColumnValues(
    datas: FlowDataOutputDto[]
  ): Promise<void> {
    if (!this._dataDependencies.hasOutputDependencies()) {
      return;
    }

    const inputs = this.qformCard.getInputs();

    for (const input of inputs) {
      if (!(input instanceof QformItemSelectorInput)) {
        continue;
      }
      if (!this._dataDependencies.hasOutputDependency(input.qflowId)) {
        continue;
      }
      const rawVal = input.value;
      if (rawVal == null || String(rawVal).trim() === '') {
        continue;
      }
      if (input.selectedColumnValues !== undefined) {
        continue;
      }

      const data = datas.find(d => d.id === input.qflowId);
      if (!data) {
        continue;
      }

      try {
        const options = await this.fetchDataSourceOptions(data);
        if (options) {
          const key = String(rawVal);
          const match = options.find(o => o.key === key);
          if (match?.columnValues) {
            input.selectedColumnValues = match.columnValues;
          }
        }
      } catch {
        // ignore
      }
    }

    this._dataDependencies.applyOutgoingDataDependencies();
  }

  protected fillFileDataWithScope(): void {
    this.qformCard
      .getItemsByType(QformFilePickerInput)
      .map(x => x as QformFilePickerInput)
      .forEach(
        fileInput =>
          (fileInput.maxSize = this.attachmentsScope!.maxAttachmentSize!)
      );
  }

  protected hasInvalidInputs(): boolean {
    const inputs = this.qformCard.getInputs();

    let hasInvalidInputs = false;

    inputs.forEach(input => {
      if (!input.isValid()) {
        hasInvalidInputs = true;
        return;
      }
    });

    return hasInvalidInputs;
  }

  private checkRequiredDataAndRoles(): boolean {
    const inputs = this.qformCard.getInputs();
    const roles = this.qformCard.getRoles();

    const hasInvalidInputs = this.checkRequiredInputs(inputs);
    const hasInvalidRoles = this.checkRequiredRoles(roles);

    return hasInvalidInputs || hasInvalidRoles;
  }

  private checkRequiredInputs(inputs: QformInputBase[]): boolean {
    let hasInvalidInputs = false;

    inputs.forEach(input => {
      if (input.scope === ItemScope.Required) {
        input.showRequired = true;
        if (!this.inputHasValue(input)) {
          hasInvalidInputs = true;
        }
      }
    });

    return hasInvalidInputs;
  }

  private checkRequiredRoles(roles: QformRole[]): boolean {
    let hasInvalidRoles = false;

    roles.forEach(role => {
      if (role.scope === ItemScope.Required) {
        role.showRequired = true;
        if (!this.isRoleValid(role)) {
          hasInvalidRoles = true;
        }
      }
    });

    return hasInvalidRoles;
  }

  private isRoleValid(role: QformRole) {
    return role.members.length > 0;
  }

  private inputHasValue(input: QformInputBase): boolean {
    switch (input.getJsonTypeName()) {
      case 'Qform.Input.File': {
        const fileInput = input as QformFilePickerInput;
        return !!input.value || !!fileInput.fileContent?.name;
      }
      case 'Qform.Input.CheckBox':
        return !!input.value && JSON.parse(input.value.toLowerCase());
      default:
        return !!input.value?.trim();
    }
  }

  protected getDataValues(): GuidStringKeyValuePair[] {
    return this.qformCard.getInputs().map(input => {
      const value = input.value?.toString();
      return new GuidStringKeyValuePair({
        key: input.qflowId,
        value: value,
      });
    });
  }

  protected getRoleMembers(): FlowRoleInputDto[] {
    return this.qformCard.getRoles().map(
      role =>
        new FlowRoleInputDto({
          id: role.qflowId,
          members: role.members.map(
            member =>
              new RoleMemberInputDto({
                id: member.id,
                templateRoleMemberType: member.type.valueOf()!,
              })
          ),
        })
    );
  }

  protected initData(dataList: FlowDataOutputDto[]) {
    const cardInputs: QformInputBase[] = this.qformCard.getInputs();
    dataList.forEach(d => {
      const inputToInit = cardInputs.find(input => input.qflowId === d.id);
      inputToInit?.init(d.domain as DomainConfigBase);
    });
  }

  protected async fillListDataOptions(dataList: FlowDataOutputDto[]) {
    this._dataDependencies.setCurrentDataList(dataList);

    const listInputs = this.qformCard
      .getInputs()
      .filter(InputHelperService.isListInput);

    for (const input of listInputs) {
      if (this._dataDependencies.hasInputDependency(input.qflowId)) continue;

      const data = dataList.find(d => d.id === input.qflowId);
      if (!data) continue;

      try {
        const options = await this.fetchDataSourceOptions(data);
        if (options) {
          this.initializeListInput(
            input as unknown as IQformListInput,
            options
          );
        } else {
          this.showError(data.name!);
        }
      } catch {
        this.showError(data.name!);
      }
    }

    this.setupCascadeListener();
    this._dataDependencies.applyOutgoingDataDependencies();
  }

  protected async fillDependentListDataOptions(dataList: FlowDataOutputDto[]) {
    this._dataDependencies.setCurrentDataList(dataList);

    const listInputs = this.qformCard
      .getInputs()
      .filter(InputHelperService.isListInput)
      .filter(input => this._dataDependencies.hasInputDependency(input.qflowId));

    for (const input of listInputs) {
      const data = dataList.find(d => d.id === input.qflowId);
      if (!data) continue;
      if (!this.hasResolvedInputDependencies(input.qflowId)) {
        continue;
      }

      try {
        const options = await this.fetchDataSourceOptions(data);
        if (options) {
          this.initializeListInput(
            input as unknown as IQformListInput,
            options
          );
          (input as any).setControlValue(data.value);
        } else {
          this.showError(data.name!);
        }
      } catch {
        this.showError(data.name!);
      }
    }

    this._dataDependencies.applyOutgoingDataDependencies();
    this._dataDependencies.syncAllKnownSourceValues();
  }

  private async fetchDataSourceOptions(
    data: FlowDataOutputDto
  ): Promise<ListOption[] | null> {
    try {
      const inputDeps = this._dataDependencies.getInputDependencies(data.id!);
      const dependencies = this.resolveDependencies(inputDeps);

      const dataSource = await this._dataDomainService.getDataSourceWithDependencies(
        data.domain.id!,
        this.versionId,
        dependencies,
        undefined
      );

      const rows = dataSource.optionRowColumnValues ?? [];
      const opts = (dataSource.options ?? [])
        .map((option, index) => ({ option, index }))
        .filter(entry => entry.option != null);
      return opts.map(entry => {
        return {
          key: entry.option.key ?? '',
          text: entry.option.value ?? '',
          columnValues: rows[entry.index],
        };
      });
    } catch {
      return null;
    }
  }

  private resolveDependencies(deps: InputDependency[]): DataDomainDependency[] {
    const inputs = this.qformCard.getInputs();
    return deps.map(dep => ({
      name: dep.parameterName,
      value: inputs.find(i => i.qflowId === dep.sourceQflowId)?.value ?? '',
    }));
  }

  private hasResolvedInputDependencies(targetQflowId: string): boolean {
    const deps = this._dataDependencies.getInputDependencies(targetQflowId);
    if (deps.length === 0) {
      return true;
    }

    const inputs = this.qformCard.getInputs();
    return deps.every(dep => {
      const sourceValue =
        inputs.find(i => i.qflowId === dep.sourceQflowId)?.value ?? null;
      return sourceValue != null && String(sourceValue).trim() !== '';
    });
  }

  private setupCascadeListener(): void {
    if (this._cascadeListenerSetup) return;
    if (
      !this._dataDependencies.hasSourceToTargets() &&
      !this._dataDependencies.hasOutputDependencies()
    ) {
      return;
    }
    this._cascadeListenerSetup = true;

    this._dataDependencies.initializeSourceValues();

    this._elementRef.nativeElement.addEventListener('change', async () => {
      const firstChangedSourceIds = await this._dataDependencies.handleCascadeChange();
      this._dataDependencies.applyOutgoingDataDependencies(firstChangedSourceIds);

      const recascade = async () => {
        const changedSourceIds = await this._dataDependencies.handleCascadeChange();
        if (changedSourceIds.length > 0) {
          this._dataDependencies.applyOutgoingDataDependencies(changedSourceIds);
          setTimeout(recascade, 0);
        }
      };

      setTimeout(recascade, 0);
    });
  }


  private initializeListInput(
    input: IQformListInput,
    options: ListOption[]
  ) {
    input.initOptions(options);
  }

  private showError(dataName: string) {
    this.toastrService.error(
      this._localizationService.localize('ErrorLoadingDataSource', dataName)
    );
  }

  protected fillRolesWithScope(flowRoles: FlowRoleOutputDto[]) {
    const roles = this.qformCard.getRoles();

    roles.forEach(role => {
      const flowRole = flowRoles.find(r => r.id === role.qflowId);
      if (flowRole) {
        this.assignRoleProperties(role, flowRole);
      } else {
        this.assignMissingScope(role);
      }
    });
  }

  private assignRoleProperties(
    role: QformRole,
    flowRole: FlowRoleOutputDto
  ): void {
    role.isMultiple = flowRole.allowsMultipleMembers ?? true;
    this.addRoleMembers(role, flowRole.members);
    this.assignScope(role, flowRole.scope);
  }

  private addRoleMembers(
    role: QformRole,
    members: TemplateRoleMemberOutputDto[] | undefined
  ): void {
    members?.forEach(member => {
      role.members.push({
        name: member.name!,
        id: member.id!,
        type: member.templateRoleMemberType!.valueOf()!,
      });
    });
  }

  private assignScope(role: QformRole, scope: any): void {
    if (scope !== undefined) {
      role.scope = scope as unknown as ItemScope;
    }
  }

  private assignMissingScope(role: QformRole): void {
    role.scope = ItemScope.Missing;
  }

  protected initCommentLog(comments: FlowCommentOutputDto[]): void {
    this.qformCard
      .getCommentLog()
      ?.init(
        comments,
        this.commentsScope!.allowAddition!,
        this.commentsScope!.viewCommentHistory!,
        this.commentsScope!.required!
      );
  }

  protected initFlowInfo(data: FlowInfoData): void {
    const l = (key: string) => this._localizationService.localize(key);
    this.qformCard.getFlowInfoItems().forEach(item => item.init({
      ...data,
      statusKey: data.status,
      status: data.status ? l(data.status) : data.status,
      importanceKey: data.importance as FlowImportanceKey | undefined,
      importance: data.importance ? l(data.importance) : data.importance,
      labelCorrelativeId: l('CorrelativeId'),
      labelFlowName: l('FlowName'),
      labelFlowDescription: l('FlowDescription'),
      labelInitiatorUser: l('InitiatorUser'),
      labelStartDate: l('StartDate'),
      labelFlowStatus: l('FlowStatus'),
      labelImportance: l('Importance'),
      labelFlag: l('Flag'),
      labelProgress: l('Progress'),
    }));
  }

  protected renderQformCard(): void {
    const components = this._componentDataService.getComponentsFromCard(
      this.qformCard,
      ViewMode.Display
    );

    this.formComponents = components.filter(component =>
      this.shouldRenderComponent(component)
    );
  }

  protected abstract getCurrentStepId(): string | undefined;

  protected shouldRenderComponent(component: ComponentData): boolean {
    if (!component.id || !component.jsonTypeName) {
      return true;
    }

    const isFormatComponent = InteractiveStepBaseComponent.FORMAT_COMPONENT_TYPES.has(component.jsonTypeName);

    if (isFormatComponent) {
      const componentScope = this.getComponentScope(component);
      if (componentScope !== undefined) {
        return componentScope !== ItemScope.Missing;
      }
    }

    const currentStepId = this.getCurrentStepId();
    if (!currentStepId) {
      return true;
    }

    if (!isFormatComponent) {
      return true;
    }

    return this.qformCard.isComponentVisibleForStep(
      component.id,
      currentStepId
    );
  }

  protected getComponentScope(
    component: ComponentData
  ): ItemScope | undefined {
    return (component as any).scope as ItemScope | undefined;
  }

  protected isValidGuid(value: string): boolean {
    const guidRegExp = new RegExp(
      /^[{]?[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}[}]?$/
    );
    return guidRegExp.test(value);
  }

  protected handleError(
    response: { success?: boolean; message?: string },
    defaultErrorMessageKey: string
  ): void {
    if (response && response.message) {
      this.toastrService.error(response.message);
    } else {
      const defaultMessage = this._localizationService.localize(
        defaultErrorMessageKey
      );
      this.toastrService.error(defaultMessage);
    }
  }

  protected getSuccessMessage(
    confirmationMessage: string | undefined,
    defaultSuccessMessageKey: string
  ): string {
    return !this.isStringNullOrEmpty(confirmationMessage)
      ? confirmationMessage!
      : this._localizationService.localize(defaultSuccessMessageKey);
  }

  protected handleException(
    error: any,
    defaultExceptionMessageKey: string
  ): void {
    const errorMessage = error.message
      ? error.message
      : this._localizationService.localize(defaultExceptionMessageKey);
    this.toastrService.error(errorMessage);
  }

  protected validateForm(commentRequired: boolean) {
    if (this.hasInvalidInputs()) {
      throw new Error(
        this._localizationService.localize('SomeInputsAreNotValid')
      );
    }

    const areRequiredInputsInvalid = this.checkRequiredDataAndRoles();
    const missingRequiredComment =
      this.checkRequiredCommentLog(commentRequired);

    if (areRequiredInputsInvalid) {
      throw new Error(this._localizationService.localize('DataValueRequired'));
    }

    if (missingRequiredComment) {
      throw new Error(this._localizationService.localize('CommentLogRequired'));
    }
  }

  private checkRequiredCommentLog(commentRequired: boolean): boolean {
    const commentLog = this.qformCard.getCommentLog();
    if (!commentLog) {
      return false;
    }

    commentLog.required = commentRequired;

    if (commentRequired) {
      commentLog.showRequired = true;
      return !commentLog.getCurrentComment();
    }

    return false;
  }

  isInIframe(): boolean {
    return window.top !== window.self;
  }

  getComment(): string | undefined {
    if (this.qformCard.getCommentLog()) {
      return this.qformCard.getCommentLog()?.getCurrentComment();
    }
    return undefined;
  }

  protected updateTitle(childTitle: string) {
    window.parent.postMessage({ name: 'setTitle', args: childTitle }, '*');
  }
}
